"use client";

import { faChevronLeft, faChevronRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Typography, formatNumber, getLocaleConstants, useClient } from "@tosize/ui";
import { chunk, shuffle } from "lodash";
import { type ReactNode, useMemo, useState } from "react";
import { Level } from "~/components/utilities/Level";

interface Props {
	lang: string;
	items: {
		name: string;
		city: string;
		date: string;
		rating: number;
		short_description: string;
		description: string;
		recommended: boolean;
		id: string;
	}[];
	prevTitle: string;
	nextTitle: string;
	children: ReactNode;
}

export function ClientReviews({ lang, items, prevTitle, nextTitle, children }: Props) {
	const localeConstants = getLocaleConstants(lang);
	const { isClient } = useClient();
	const [page, setPage] = useState(1);
	const itemsPerPage = 2;
	const filteredItems = useMemo(
		() => chunk(isClient ? shuffle(items) : items, itemsPerPage)[page - 1],
		[isClient, items, page],
	);

	if (!filteredItems?.length) {
		return null;
	}

	return (
		<>
			<div className="flex items-center justify-between">
				{children}

				<div className="flex gap-2">
					<button
						type="button"
						className="h-8 w-8 cursor-pointer appearance-none items-center justify-center rounded-full bg-shades-light enter:bg-primary enter:text-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 motion-safe:transition"
						onClick={() => setPage((prev) => prev - 1)}
						disabled={page === 1}
						title={prevTitle}
					>
						<FontAwesomeIcon icon={faChevronLeft} />
					</button>

					<button
						type="button"
						className="h-8 w-8 cursor-pointer appearance-none items-center justify-center rounded-full bg-shades-light enter:bg-primary enter:text-white focus:outline-none disabled:cursor-not-allowed disabled:opacity-50 motion-safe:transition"
						onClick={() => setPage((prev) => prev + 1)}
						disabled={page * itemsPerPage >= items.length}
						title={nextTitle}
					>
						<FontAwesomeIcon icon={faChevronRight} />
					</button>
				</div>
			</div>

			<div className="flex flex-col gap-6">
				{filteredItems.map((item) => (
					<div key={item.id} className="flex flex-col gap-1 rounded border border-shades p-4">
						<div className="flex gap-4">
							<Level rating={item.rating} />
							<Typography color="text-shades-dark">
								{formatNumber({
									lang,
									value: localeConstants.kiyoh.totalScore === 5 ? item.rating / 2 : item.rating,
									style: "decimal",
									digits: { min: 0, max: 1 },
								})}
							</Typography>
						</div>

						<Typography variant="paragraph-bold">{item.short_description}</Typography>
						<Typography className="min-h-[calc(1.5em*4)]">{item.description}</Typography>

						<Typography
							color="text-shades-dark"
							className="flex xs:flex-row flex-wrap items-center gap-x-4"
						>
							<span>{item.name}</span>
						</Typography>
					</div>
				))}
			</div>
		</>
	);
}
